import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../firebase/AuthProvider';
import { Conversation, Message, getThreadReplies } from '../../../services/chatService/ChatService';
import { useChatConnection } from '../../../contexts/ChatConnectionContext';
import { ValidateOtpResponseDto } from '../../../interfaces/ExtermalUserDtos/ExternalUsersDtos';
import { getUTCTime } from '../../../utilities/dateUtils';
import { ArrowLeft, LockKeyhole } from 'lucide-react';
import MessageItem from './MessageItem';
import ThreadReplyEditor, { ThreadReplyEditorRef } from './ThreadReplyEditor';
import ChatMessageSkeleton from './ChatMessageSkeleton';
import { toast } from 'react-toastify';
import { getMessageGroupingPositions } from '../../../utilities/chat/groupingUtils';

import { useVisibleReactionRead } from "../../../hooks/globalChatHooks/useVisibleReactionRead";

interface ThreadPanelProps {
    readTrackingEnabled?: boolean;
    rootMessage: Message;
    focusedMessageId?: number;
    conversation: Conversation;
    onClose: () => void;
    externalUserDetails?: ValidateOtpResponseDto | null;
    canWrite?: boolean;
    readOnlyReason?: string;
    readOnlyTitle?: string;
    appearance?: "default" | "meeting-dark";
    singlePane?: boolean;
    onToggleBookmark?: (messageId: number) => Promise<boolean>;
    onReadThrough?: (messageId: number) => void;
}

const ThreadPanel: React.FC<ThreadPanelProps> = ({
    readTrackingEnabled = true,
    rootMessage,
    focusedMessageId,
    conversation,
    onClose,
    externalUserDetails,
    canWrite = false,
    readOnlyReason = "You have read-only access to this channel.",
    readOnlyTitle = "Replies are read only",
    appearance = "default",
    singlePane = false,
    onToggleBookmark,
    onReadThrough,
}) => {
    const isMeetingDark = appearance === "meeting-dark";
    const { currentUser } = useAuth();
    const { connection } = useChatConnection();

    const [replies, setReplies] = useState<Message[]>([]);
    const [displayRootMessage, setDisplayRootMessage] = useState(rootMessage);
    const [toggledReactionMenuId, setToggledReactionMenuId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [highlightedMessageId, setHighlightedMessageId] = useState<number | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    useVisibleReactionRead(scrollRef, conversation.id, "data-thread-message-id", replies, readTrackingEnabled && !isLoading);
    const revealedFocusedMessageRef = useRef<number | null>(null);
    const replyEditorRef = useRef<ThreadReplyEditorRef>(null);
    const reactionMutationVersionsRef = useRef(new Map<string, number>());

    const currentMyId = currentUser?.uid || externalUserDetails?.externalUserDetailId;

    useEffect(() => setDisplayRootMessage(rootMessage), [rootMessage]);

    useEffect(() => {
        const fetchReplies = async () => {
            setIsLoading(true);
            try {
                const token = currentUser ? await currentUser.getIdToken() : "";
                const data = await getThreadReplies(token, rootMessage.id);

                // 1. Filter out the root message since we already render it explicitly at the top
                const repliesOnly = data.filter((msg: Message) => msg.id !== rootMessage.id);

                // 2. Map and sort ONLY the replies
                const normalized = repliesOnly.map((msg: Message) => ({
                    ...msg,
                    createDate: new Date(getUTCTime(msg.createDate))
                }));

                normalized.sort((a: Message, b: Message) => a.createDate.getTime() - b.createDate.getTime());
                setReplies(normalized);
            } catch (err) {
                console.error("Failed to load thread replies", err);
            } finally {
                setIsLoading(false);
            }
        };
        fetchReplies();
    }, [rootMessage.id, currentUser]);

    useEffect(() => {
        if (!connection) return;

        const handleReceiveMessage = (msg: Message) => {
            if (msg.parentMessageId === rootMessage.id) {
                setReplies(prev => {
                    if (prev.some(message => message.id === msg.id)) {
                        return prev.flatMap(message => {
                            const isMatchingOptimistic = message.isOptimistic && Boolean(
                                msg.clientMessageId && message.clientMessageId === msg.clientMessageId
                            );
                            if (isMatchingOptimistic) {
                                replyEditorRef.current?.acknowledgeMessage(message.id);
                                if (typeof message.attachmentUrl === "string" && message.attachmentUrl.startsWith("blob:")) {
                                    URL.revokeObjectURL(message.attachmentUrl);
                                }
                                return [];
                            }
                            return [message.id === msg.id
                                ? { ...message, ...msg, createDate: new Date(msg.createDate), isOptimistic: false, deliveryStatus: "sent" }
                                : message];
                        });
                    }
                    const isMyMessage = msg.author?.id === currentMyId;

                    let foundOptimisticMatch = false;
                    const filtered = prev.filter((m: any) => {
                        const clientIdMatches = Boolean(
                            msg.clientMessageId &&
                            m.clientMessageId &&
                            msg.clientMessageId === m.clientMessageId
                        );
                        const contentMatches = (m.messageContent || "").trim() === (msg.messageContent || "").trim();
                        const attachmentMatches = Boolean(m.attachmentUrl) === Boolean(msg.attachmentUrl);
                        if (m.isOptimistic && isMyMessage && (clientIdMatches || (contentMatches && attachmentMatches)) && !foundOptimisticMatch) {
                            foundOptimisticMatch = true;
                            if (typeof m.attachmentUrl === "string" && m.attachmentUrl.startsWith("blob:")) URL.revokeObjectURL(m.attachmentUrl);
                            replyEditorRef.current?.acknowledgeMessage(m.id);
                            return false;
                        }
                        return true;
                    });

                    const normalizedMsg = { ...msg, createDate: new Date(msg.createDate), isNew: true };
                    return [...filtered, normalizedMsg].sort((a, b) => a.createDate.getTime() - b.createDate.getTime());
                });
            }
        };

        const handleReceiveMessageDelete = (update: { conversationId?: number; ConversationId?: number; messageId?: number; MessageId?: number }) => {
            if ((update.conversationId ?? update.ConversationId) !== conversation.id) return;
            const messageId = update.messageId ?? update.MessageId;
            if (!messageId) return;
            setDisplayRootMessage(message => message.id === messageId ? { ...message, isDeleted: true } : message);
            setReplies(messages => messages.map(message => message.id === messageId ? { ...message, isDeleted: true } : message));
        };

        const handleReceiveMessageUpdate = (update: any) => {
            if ((update.conversationId ?? update.ConversationId) !== conversation.id) return;
            const messageId = update.messageId ?? update.MessageId;
            if (!messageId) return;
            const patch = {
                messageContent: update.newContent,
                messagePlaintext: update.messagePlaintext ?? update.newContent,
                messageDoc: update.messageDoc,
                isEdited: true,
            };
            setDisplayRootMessage(message => message.id === messageId ? { ...message, ...patch } : message);
            setReplies(messages => messages.map(message => message.id === messageId ? { ...message, ...patch } : message));
        };

        connection.on("ReceiveMessage", handleReceiveMessage);
        connection.on("ReceiveMessageDelete", handleReceiveMessageDelete);
        connection.on("ReceiveMessageUpdate", handleReceiveMessageUpdate);
        return () => {
            connection.off("ReceiveMessage", handleReceiveMessage);
            connection.off("ReceiveMessageDelete", handleReceiveMessageDelete);
            connection.off("ReceiveMessageUpdate", handleReceiveMessageUpdate);
        };
    }, [connection, conversation.id, rootMessage.id, currentMyId]);

    useEffect(() => {
        if (!connection) return;

        const applyReaction = (message: Message, messageId: number, userId: string, reactionType: string | null, action: string): Message => {
            if (message.id !== messageId) return message;
            const existing = message.reactions || [];
            const withoutUser = existing.filter((reaction) => reaction.userId !== userId);
            if (action === "removed" || !reactionType) return { ...message, reactions: withoutUser };

            return {
                ...message,
                reactions: [
                    ...withoutUser,
                    {
                        userId,
                        reactionType,
                        fullName: userId === currentMyId ? "You" : "",
                    },
                ],
            };
        };

        const handleReceiveReaction = (
            conversationId: number,
            messageId: number,
            userId: string,
            reactionType: string | null,
            action: string,
        ) => {
            if (conversationId !== conversation.id) return;
            setDisplayRootMessage((message) => applyReaction(message, messageId, userId, reactionType, action));
            setReplies((messages) => messages.map((message) => applyReaction(message, messageId, userId, reactionType, action)));
        };

        const handleBookmarkUpdate = (update: { messageId?: number; MessageId?: number; isBookmarked?: boolean; IsBookmarked?: boolean }) => {
            const messageId = update.messageId ?? update.MessageId;
            if (!messageId) return;
            const isBookmarked = update.isBookmarked ?? update.IsBookmarked ?? false;
            setDisplayRootMessage(message => message.id === messageId ? { ...message, isBookmarked } : message);
            setReplies(messages => messages.map(message => message.id === messageId ? { ...message, isBookmarked } : message));
        };

        connection.on("ReceiveReaction", handleReceiveReaction);
        connection.on("ReceiveBookmarkUpdate", handleBookmarkUpdate);
        return () => {
            connection.off("ReceiveReaction", handleReceiveReaction);
            connection.off("ReceiveBookmarkUpdate", handleBookmarkUpdate);
        };
    }, [connection, conversation.id, currentMyId]);

    useEffect(() => {
        if (!focusedMessageId && scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [focusedMessageId, replies]);

    useEffect(() => {
        if (isLoading || !focusedMessageId || !scrollRef.current) return;
        if (revealedFocusedMessageRef.current === focusedMessageId) return;
        let frame = 0;
        let clearHighlightTimer = 0;
        let attempts = 0;
        const revealTarget = () => {
            const target = scrollRef.current?.querySelector<HTMLElement>(`[data-thread-message-id="${focusedMessageId}"]`);
            if (!target) {
                attempts += 1;
                if (attempts < 30) frame = window.requestAnimationFrame(revealTarget);
                return;
            }
            const container = scrollRef.current!;
            const containerRect = container.getBoundingClientRect();
            const targetRect = target.getBoundingClientRect();
            const targetTop = container.scrollTop + targetRect.top - containerRect.top
                - (container.clientHeight - targetRect.height) / 2;
            container.scrollTo({ top: Math.max(0, targetTop), behavior: 'auto' });
            revealedFocusedMessageRef.current = focusedMessageId;
            setHighlightedMessageId(focusedMessageId);
            clearHighlightTimer = window.setTimeout(() => setHighlightedMessageId(null), 3000);
        };
        frame = window.requestAnimationFrame(revealTarget);
        return () => {
            window.cancelAnimationFrame(frame);
            window.clearTimeout(clearHighlightTimer);
        };
    }, [focusedMessageId, isLoading, replies]);

    useEffect(() => {
        if (revealedFocusedMessageRef.current !== focusedMessageId) {
            revealedFocusedMessageRef.current = null;
        }
    }, [focusedMessageId]);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container || !onReadThrough || isLoading || !readTrackingEnabled) return;
        let readTimer = 0;
        const messageTimes = new Map([displayRootMessage, ...replies].map(message => [message.id, getUTCTime(message.createDate)]));
        const observer = new IntersectionObserver((entries) => {
            window.clearTimeout(readTimer);
            if (document.visibilityState !== "visible") return;
            const visibleIds = entries
                .filter(entry => entry.isIntersecting && entry.intersectionRatio >= 0.55)
                .map(entry => Number((entry.target as HTMLElement).dataset.threadMessageId))
                .filter(messageId => messageId > 0);
            if (visibleIds.length === 0) return;
            const latestVisibleMessageId = visibleIds.sort((left, right) =>
                (messageTimes.get(right) ?? 0) - (messageTimes.get(left) ?? 0) || right - left
            )[0];
            readTimer = window.setTimeout(() => {
                if (document.visibilityState === "visible") onReadThrough(latestVisibleMessageId);
            }, 400);
        }, { root: container, threshold: [0.55, 0.8, 1] });
        container.querySelectorAll<HTMLElement>("[data-thread-message-id]").forEach(element => observer.observe(element));
        return () => {
            window.clearTimeout(readTimer);
            observer.disconnect();
        };
    }, [displayRootMessage, isLoading, onReadThrough, replies, readTrackingEnabled]);

    const handleSendReply = (optimisticMsg: any) => {
        setReplies(prev => [...prev, optimisticMsg]);
    };

    const handleUpdateOptimistic = (messageId: number, patch: Record<string, unknown>) => {
        setDisplayRootMessage(message => message.id === messageId ? { ...message, ...patch } : message);
        setReplies(previous => previous.map(message => message.id === messageId ? { ...message, ...patch } : message));
    };

    const handleCancelOptimistic = (messageId: number) => {
        replyEditorRef.current?.cancelMessage(messageId);
        setReplies(previous => previous.filter(message => message.id !== messageId));
    };

    const handleToggleReactionMenu = (messageId: number) => {
        setToggledReactionMenuId((current) => current === messageId ? null : messageId);
    };

    const handleAddReaction = (messageId: number, reactionType: string) => {
        setToggledReactionMenuId(null);
        if (!connection || !currentMyId) return;
        const previousRoot = displayRootMessage;
        const previousReplies = replies;
        const target = displayRootMessage.id === messageId
            ? displayRootMessage
            : replies.find(message => message.id === messageId);
        if (!target) return;
        const existing = target.reactions || [];
        const hasReacted = existing.some(reaction => reaction.userId === currentMyId && reaction.reactionType === reactionType);
        const optimisticReactions = hasReacted
            ? existing.filter(reaction => !(reaction.userId === currentMyId && reaction.reactionType === reactionType))
            : [...existing.filter(reaction => reaction.userId !== currentMyId), { userId: currentMyId, reactionType, fullName: "You" }];
        const mutationKey = `${messageId}:${currentMyId}`;
        const mutationVersion = (reactionMutationVersionsRef.current.get(mutationKey) ?? 0) + 1;
        reactionMutationVersionsRef.current.set(mutationKey, mutationVersion);
        setDisplayRootMessage(message => message.id === messageId ? { ...message, reactions: optimisticReactions } : message);
        setReplies(messages => messages.map(message => message.id === messageId ? { ...message, reactions: optimisticReactions } : message));

        connection.invoke("SendReaction", messageId, reactionType).catch((error) => {
            console.error("Failed to update thread reaction", error);
            if (reactionMutationVersionsRef.current.get(mutationKey) !== mutationVersion) return;
            setDisplayRootMessage(previousRoot);
            setReplies(previousReplies);
            toast.error("Reaction could not be updated.");
        });
    };

    const handleToggleBookmark = async (messageId: number) => {
        if (!onToggleBookmark) return;
        const target = displayRootMessage.id === messageId
            ? displayRootMessage
            : replies.find(message => message.id === messageId);
        const previousValue = Boolean(target?.isBookmarked);
        const optimisticValue = !previousValue;
        setDisplayRootMessage(message => message.id === messageId ? { ...message, isBookmarked: optimisticValue } : message);
        setReplies(messages => messages.map(message => message.id === messageId ? { ...message, isBookmarked: optimisticValue } : message));
        try {
            const isBookmarked = await onToggleBookmark(messageId);
            setDisplayRootMessage(message => message.id === messageId ? { ...message, isBookmarked } : message);
            setReplies(messages => messages.map(message => message.id === messageId ? { ...message, isBookmarked } : message));
        } catch {
            setDisplayRootMessage(message => message.id === messageId ? { ...message, isBookmarked: previousValue } : message);
            setReplies(messages => messages.map(message => message.id === messageId ? { ...message, isBookmarked: previousValue } : message));
        }
    };

    const handleDeleteMessage = async (messageId: number) => {
        const previousRoot = displayRootMessage.id === messageId ? displayRootMessage : null;
        const previousReply = replies.find(message => message.id === messageId) ?? null;
        if ((!previousRoot && !previousReply) || previousRoot?.isDeleted || previousReply?.isDeleted) return;

        setDisplayRootMessage(message => message.id === messageId ? { ...message, isDeleted: true } : message);
        setReplies(messages => messages.map(message => message.id === messageId ? { ...message, isDeleted: true } : message));

        try {
            const token = currentUser ? await currentUser.getIdToken() : "";
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/chat/message/${messageId}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            const result = await response.json();
            if (!response.ok || !result.success) throw new Error(result.message || "Failed to delete message.");
            toast.success("Message deleted");
        } catch (error: any) {
            if (previousRoot) setDisplayRootMessage(message => ({ ...message, isDeleted: previousRoot.isDeleted }));
            if (previousReply) setReplies(messages => messages.map(message => message.id === messageId ? { ...message, isDeleted: previousReply.isDeleted } : message));
            toast.error(error?.message || "Error deleting message.");
        }
    };

    const replyGroupingPositions = getMessageGroupingPositions(replies);

    return (
        <div className={`thread-panel-container relative z-20 flex h-full w-full flex-col ${singlePane ? 'border-l-0 shadow-none' : 'border-l shadow-xl'} ${isMeetingDark ? 'bg-slate-950 border-slate-800' : 'bg-white border-slate-200 dark:border-white/[0.06] dark:bg-[#11131c]'}`}>
            <div className={`dark-surface chat-thread-header flex items-center gap-3 px-3 py-2.5 shrink-0 ${isMeetingDark ? 'bg-slate-900' : 'bg-slate-50 dark:bg-[#171a24]'}`}>
                <button onClick={onClose} className={`chat-thread-back p-1.5 rounded-lg transition-colors shrink-0 ${isMeetingDark ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-100' : 'text-slate-500 hover:bg-slate-200 hover:text-slate-800'}`}>
                    <ArrowLeft size={18} strokeWidth={2.5} />
                </button>
                <div className="flex flex-col min-w-0">
                    <h3 className={`chat-thread-title text-[14.5px] font-semibold truncate m-0 ${isMeetingDark ? 'text-slate-100' : 'text-slate-800'}`}>Thread</h3>
                    <span className={`chat-thread-subtitle text-[11px] font-medium truncate m-0 ${isMeetingDark ? 'text-slate-500' : 'text-slate-500'}`}>
                        {conversation.channel?.name || "Direct Message"}
                    </span>
                </div>
            </div>

            <div className={`chat-thread-body flex-1 overflow-y-auto custom-scrollbar ${isMeetingDark ? 'bg-slate-950' : 'bg-white dark:bg-[#11131c]'}`} ref={scrollRef}>
                <div data-thread-message-id={displayRootMessage.id} className={`pt-4 pb-2 border-b-[1px] mb-2 transition ${highlightedMessageId === displayRootMessage.id ? 'bg-purple-50 ring-2 ring-inset ring-purple-300 dark:bg-purple-400/10' : ''} ${isMeetingDark ? 'border-slate-800' : 'border-slate-100'}`}>
                    <MessageItem
                        message={displayRootMessage}
                        toggledReactionMenuId={toggledReactionMenuId}
                        onToggleReactionMenu={handleToggleReactionMenu}
                        onAddReaction={handleAddReaction}
                        isDateVisible={false}
                        onToggleDate={() => { }}
                        externalUserDetails={externalUserDetails}
                        appearance={appearance}
                        inThread
                        onToggleBookmark={onToggleBookmark ? (messageId) => void handleToggleBookmark(messageId) : undefined}
                        onDeleteMessage={(messageId) => void handleDeleteMessage(messageId)}
                        onEditMessage={(messageId) => {
                            if (displayRootMessage.id === messageId) replyEditorRef.current?.beginEdit(displayRootMessage);
                        }}
                    />
                </div>

                <div className="px-4 py-2">
                    <div className="flex items-center gap-3 mb-4">
                        <div className={`h-px flex-1 ${isMeetingDark ? 'bg-slate-800' : 'bg-slate-200'}`}></div>
                        <span className={`text-[11px] font-bold uppercase tracking-wider ${isMeetingDark ? 'text-slate-600' : 'text-slate-400'}`}>{replies.length} Replies</span>
                        <div className={`h-px flex-1 ${isMeetingDark ? 'bg-slate-800' : 'bg-slate-200'}`}></div>
                    </div>
                </div>

                <div className="flex flex-col pb-4">
                    {isLoading ? (
                        <ChatMessageSkeleton rows={3} compact />
                    ) : (
                        replies.map((reply, replyIndex) => {
                            const grouping = replyGroupingPositions[replyIndex];
                            return (
                            <div key={reply.id} data-thread-message-id={reply.id} className={`rounded-xl transition ${highlightedMessageId === reply.id ? 'bg-purple-50 ring-2 ring-inset ring-purple-300 dark:bg-purple-400/10' : ''}`}>
                                <MessageItem
                                    message={reply}
                                    toggledReactionMenuId={toggledReactionMenuId}
                                    onToggleReactionMenu={handleToggleReactionMenu}
                                    onAddReaction={handleAddReaction}
                                    isDateVisible={false}
                                    onToggleDate={() => { }}
                                    externalUserDetails={externalUserDetails}
                                    appearance={appearance}
                                    inThread
                                    onToggleBookmark={onToggleBookmark ? (messageId) => void handleToggleBookmark(messageId) : undefined}
                                    onRetryMessage={(messageId) => replyEditorRef.current?.retryMessage(messageId)}
                                    onCancelMessage={handleCancelOptimistic}
                                    onDeleteMessage={(messageId) => void handleDeleteMessage(messageId)}
                                    onEditMessage={(messageId) => {
                                        const message = replies.find(candidate => candidate.id === messageId);
                                        if (message) replyEditorRef.current?.beginEdit(message);
                                    }}
                                    groupedWithPrevious={grouping.groupedWithPrevious}
                                    groupedWithNext={grouping.groupedWithNext}
                                />
                            </div>
                            );
                        })
                    )}
                </div>
            </div>

            <div className={`chat-thread-footer dark-surface shrink-0 p-0 ${isMeetingDark ? 'bg-slate-900' : 'bg-slate-50 dark:bg-[#171a24]'}`}>
                {canWrite ? (
                    <ThreadReplyEditor
                        ref={replyEditorRef}
                        rootMessage={rootMessage}
                        conversation={conversation}
                        onSendOptimistic={handleSendReply}
                        onUpdateOptimistic={handleUpdateOptimistic}
                        externalUserDetails={externalUserDetails}
                        appearance={appearance}
                    />
                ) : (
                    <div
                        role="status"
                        className={`chat-thread-readonly ${isMeetingDark ? 'chat-thread-readonly--dark' : ''} flex items-start gap-3 rounded-xl border-[1px] px-3 py-3 ${isMeetingDark ? 'border-[#7e57c2]/30 bg-slate-800 text-purple-200' : 'border-[#7e57c2]/20 bg-white text-[#6847a3]'}`}
                    >
                        <LockKeyhole className="mt-0.5 shrink-0" size={16} />
                        <div>
                            <p className="chat-thread-readonly__title m-0 text-xs font-semibold">{readOnlyTitle}</p>
                            <p className={`chat-thread-readonly__body mb-0 mt-1 text-[11px] leading-relaxed ${isMeetingDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                {readOnlyReason}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ThreadPanel;
